import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Heart,
  Wrench,
  Building2,
  Stethoscope,
  Users as UsersIcon,
  Building,
  Globe,
  GraduationCap,
  Activity,
  BookOpen,
  CheckCircle2,
} from 'lucide-react';
import '../styles/FreeGuidePage.css';
import '../styles/EbookSalesPage.css';
import '../styles/LandingPage.css';
import ServicesMarquee from '../components/ServicesMarquee';
import BackToTop from '../components/BackToTop';

const GUMROAD_URL = 'https://africasaroots.gumroad.com/l/culturally-responsive-practice-guide';

const audience = [
  { icon: <Stethoscope size={20} />, label: 'Licensed therapists & counselors' },
  { icon: <UsersIcon size={20} />, label: 'Social workers & case managers' },
  { icon: <Building size={20} />, label: 'Behavioral health organizations' },
  { icon: <Globe size={20} />, label: 'Refugee resettlement providers' },
  { icon: <GraduationCap size={20} />, label: 'Clinical supervisors & trainers' },
  { icon: <Activity size={20} />, label: 'Healthcare providers & nurses' },
];

const inside = [
  {
    icon: <Search size={26} />,
    title: 'Identify Your Cultural Gaps',
    text: 'A self-assessment framework to honestly evaluate where your current practice falls short — and a roadmap to close those gaps.',
  },
  {
    icon: <Heart size={26} />,
    title: 'Trauma-Informed Approaches',
    text: 'Evidence-based, culturally adapted strategies for working with trauma in refugee and immigrant populations — without causing harm.',
  },
  {
    icon: <Wrench size={26} />,
    title: 'Practical Tools to Apply Now',
    text: 'Frameworks, conversation guides, and clinical tools you can start using in your very next session — no lengthy training required.',
  },
  {
    icon: <Building2 size={26} />,
    title: 'Building Truly Responsive Services',
    text: 'A systems-level framework for organizations wanting to embed cultural responsiveness into intake, assessment, and care delivery.',
  },
];

const credentials = ['LMSW', 'Bilingual (English/French)', 'Refugee & Immigrant Services', 'Behavioral Health Consulting'];

const EbookSalesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'The Culturally Responsive Practice Guide | AfriCasa Behavioral Health';
  }, []);

  return (
    <div className="landing-page fg-page ebook-sales-page">
      {/* Header */}
      <header className="fg-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="logo-container fg-logo-link" data-testid="ebook-home-link">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="logo"
              />
            </Link>
            <Link to="/" className="fg-back-link" data-testid="ebook-back-link">
              <ArrowLeft size={18} />
              <span>BACK TO HOME</span>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="fg-hero ebook-hero" data-testid="ebook-hero">
        <div className="container mx-auto px-6">
          <div className="fg-hero-inner ebook-hero-inner">
            <p className="fg-eyebrow">New Release · Limited Sale Price</p>
            <h1 className="fg-hero-title ebook-hero-title" data-testid="ebook-hero-title">
              Your Clinical Skills Should Actually Reach The Communities You Serve.
            </h1>
            <div className="fg-divider" />
            <p className="fg-hero-subtitle">
              A practical guide for clinicians and providers working with refugee and immigrant
              populations — built to close the cultural gap between good intentions and real impact.
            </p>

            {/* Hero pricing card — styled to match the final CTA card */}
            <div className="ebook-hero-pricing-card" data-testid="ebook-hero-pricing-card">
              <div className="ebook-hero-card-icon"><BookOpen size={26} /></div>
              <p className="ebook-final-regular">Regular price <span className="ebook-final-strike">$57</span></p>
              <p className="ebook-final-price">$47</p>
              <p className="ebook-final-meta">One-time purchase · Instant PDF download</p>
              <a
                href={GUMROAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold ebook-final-cta-btn"
                data-testid="ebook-hero-cta-btn"
              >
                Get The Guide — $47
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="ebook-divider-hero-problem" />

      {/* THE PROBLEM */}
      <section className="fg-section fg-section-problem ebook-problem" data-testid="ebook-section-problem">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner">
            <p className="fg-label">The Problem</p>
            <h2 className="fg-section-headline">Good Training Alone Isn&apos;t Enough.</h2>
            <div className="fg-section-body">
              <p>
                Most clinicians working with refugee and immigrant communities were never trained to
                address the cultural, linguistic, and trauma-specific barriers their clients face
                every day.
              </p>
              <p>
                You may have the clinical credentials — but without culturally responsive
                frameworks, your interventions may miss the mark entirely. Clients disengage. Trust
                breaks down. Outcomes suffer.
              </p>
            </div>

            <blockquote className="ebook-quote" data-testid="ebook-quote">
              Going beyond good intentions means meeting refugee and immigrant clients exactly where
              they are — culturally, linguistically, and emotionally.
            </blockquote>

            <p className="fg-section-body ebook-problem-closer">
              This guide gives you the frameworks, tools, and language to bridge that gap — so the
              care you provide finally lands where it is needed most.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="fg-section ebook-audience" data-testid="ebook-section-audience">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner">
            <p className="fg-label">This Guide Is For You If…</p>
            <h2 className="fg-section-headline">
              You Work With Refugees And Immigrants — And Want To Do It Better.
            </h2>
            <p className="ebook-audience-lede">
              Whether you are a seasoned clinician or just starting to serve these communities, this
              guide gives you practical, culturally grounded tools you can apply immediately.
            </p>

            <ul className="ebook-audience-grid" data-testid="ebook-audience-grid">
              {audience.map((a, i) => (
                <li key={i} className="ebook-audience-card" data-testid={`audience-${i}`}>
                  <span className="ebook-audience-icon">{a.icon}</span>
                  <span className="ebook-audience-label">{a.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT IS INSIDE */}
      <section className="fg-section fg-section-alt" data-testid="ebook-section-inside">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner">
            <p className="fg-label">What&apos;s Inside</p>
            <h2 className="fg-section-headline">
              Everything You Need To Practice With Cultural Precision.
            </h2>
            <p className="ebook-inside-lede">
              A 31-page PDF guide packed with frameworks, tools, and strategies you can use
              immediately in your practice.
            </p>

            <div className="fg-walkaway-grid" data-testid="ebook-inside-grid">
              {inside.map((item, i) => (
                <div key={i} className="fg-walkaway-card" data-testid={`inside-card-${i}`}>
                  <div className="fg-walkaway-icon">{item.icon}</div>
                  <h3 className="fg-walkaway-title">{item.title}</h3>
                  <p className="fg-walkaway-text">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="ebook-divider-inside-why" />

      {/* MOCKUP + WHAT YOU'LL LEARN — single centered vertical stack */}
      <section className="fg-section ebook-combined-section" data-testid="ebook-section-combined">
        <div className="container mx-auto px-6">
          <div className="ebook-combined-stack">
            {/* 1. Eyebrow label + Tagline at the very top (headline-sized) */}
            <p className="fg-label fg-label-centered" data-testid="ebook-combined-eyebrow">Why It Matters</p>
            <h2 className="fg-section-headline ebook-combined-tagline" data-testid="ebook-combined-tagline">
              Close The Gap Between Good Intentions And Real Impact.
            </h2>

            {/* 2. Mockup image centered */}
            <div className="ebook-combined-image" data-testid="ebook-cover-image-wrapper">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/fk35zpih_Brown%20and%20White%20Marble%20Surface%20Website%20Mockup%20Instagram%20Post%20%281%29.png"
                alt="The Culturally Responsive Practice Guide by Vanessa Diambou, LMSW — $57 now $47"
                className="ebook-cover-image"
                data-testid="ebook-cover-image"
                draggable={false}
              />
            </div>

            {/* 3 + 4. Label and title centered */}
            <div className="ebook-combined-heading">
              <p className="fg-label fg-label-centered">Inside The Guide</p>
              <h2 className="fg-section-headline ebook-learn-headline">What You&apos;ll Learn.</h2>
            </div>

            {/* 5. Five gold checkmark boxes */}
            <ul className="ebook-learn-list" data-testid="ebook-learn-list">
              <li className="ebook-learn-item" data-testid="learn-item-0">
                <CheckCircle2 className="ebook-learn-check" size={26} aria-hidden="true" />
                <span>How to have culturally informed conversations with refugee and immigrant clients</span>
              </li>
              <li className="ebook-learn-item" data-testid="learn-item-1">
                <CheckCircle2 className="ebook-learn-check" size={26} aria-hidden="true" />
                <span>How to recognize and navigate cultural mismatches in clinical settings</span>
              </li>
              <li className="ebook-learn-item" data-testid="learn-item-2">
                <CheckCircle2 className="ebook-learn-check" size={26} aria-hidden="true" />
                <span>How to adapt your existing clinical skills to diverse cultural contexts</span>
              </li>
              <li className="ebook-learn-item" data-testid="learn-item-3">
                <CheckCircle2 className="ebook-learn-check" size={26} aria-hidden="true" />
                <span>How to move beyond good intentions and deliver care that actually lands</span>
              </li>
              <li className="ebook-learn-item" data-testid="learn-item-4">
                <CheckCircle2 className="ebook-learn-check" size={26} aria-hidden="true" />
                <span>How to build lasting trust with clients who have experienced systemic barriers</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ABOUT THE AUTHOR */}
      <section className="fg-section ebook-author" data-testid="ebook-section-author">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner ebook-author-inner">
            <p className="fg-label fg-label-centered">Your Guide</p>
            <h2 className="fg-section-headline ebook-author-headline">
              Written By Someone Who Has Lived This Work.
            </h2>

            <div className="ebook-author-body">
              <p>
                <strong>Vanessa Diambou, LMSW</strong> is the founder of AfriCasa Behavioral Health
                Consulting — a practice dedicated to bridging the gap between mental health systems
                and the communities they too often fail.
              </p>
              <p>
                With over 10 years of clinical experience across inpatient psychiatry, crisis
                intervention, and community mental health, Vanessa brings both clinical expertise
                and lived experience as an immigrant to her work. She is bilingual in French and
                English and has direct experience serving refugee and immigrant populations.
              </p>
              <p className="ebook-author-emphasis">
                This guide was built from real frontline practice.
              </p>
            </div>

            <ul className="ebook-credentials" data-testid="ebook-credentials">
              {credentials.map((c, i) => (
                <li key={i} className="ebook-credential-chip">{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="ebook-divider-author-cta" />

      {/* FINAL CTA */}
      <section className="fg-section ebook-final-cta" data-testid="ebook-section-final-cta">
        <div className="container mx-auto px-6">
          <div className="ebook-final-card">
            <h2 className="ebook-final-headline">
              Ready To Practice With Real Cultural Responsiveness?
            </h2>
            <p className="ebook-final-sub">
              Join clinicians and organizations already using this guide to close the gap between
              training and impact.
            </p>

            <div className="ebook-final-pricing" data-testid="ebook-final-pricing">
              <p className="ebook-final-regular">Regular price <span className="ebook-final-strike">$57</span></p>
              <p className="ebook-final-price">$47</p>
              <p className="ebook-final-meta">One-time purchase · Instant PDF download</p>
            </div>

            <a
              href={GUMROAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold ebook-final-cta-btn"
              data-testid="ebook-final-cta-btn"
            >
              Get Instant Access — $47
              <ArrowRight size={18} />
            </a>

            <p className="ebook-final-note">
              Instant download after purchase.
              <br />
              Questions?
              <br />
              <a href="mailto:info@africasabehavioralhealth.org">info@africasabehavioralhealth.org</a>
            </p>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="ebook-divider-cta-footer" />

      {/* Footer — mirrors the homepage footer */}
      <footer className="footer">
        <div className="container mx-auto px-6">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="footer-logo-img"
              />
              <p className="footer-tagline">Culturally Rooted • Clinically Grounded</p>
            </div>

            <div className="footer-links">
              <div>
                <h4 className="footer-heading">Services</h4>
                <ul className="footer-list">
                  <li><Link to="/cultural-needs-assessment">Cultural Needs Assessment</Link></li>
                  <li><Link to="/cultural-competency-consulting">Cultural Competency Consulting</Link></li>
                  <li><Link to="/case-management">Organizational Case Management Services</Link></li>
                  <li><Link to="/concierge-case-management">Concierge Case Management Services</Link></li>
                  <li><Link to="/culturally-responsive-coaching">Culturally Responsive Coaching</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Company</h4>
                <ul className="footer-list">
                  <li><Link to="/#about">About</Link></li>
                  <li><Link to="/#who-we-serve">Who We Serve</Link></li>
                  <li><Link to="/#services">Services</Link></li>
                  <li><Link to="/#certifications">Certifications</Link></li>
                  <li><Link to="/#reviews">Reviews</Link></li>
                  <li><Link to="/#contact">Contact</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Contact</h4>
                <ul className="footer-list">
                  <li><a href="mailto:info@africasabehavioralhealth.org">info@africasabehavioralhealth.org</a></li>
                  <li><a href="tel:+18024885307">(802) 488-5307</a></li>
                  <li>Providence, RI</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 AfriCasaRoots LLC (DBA AfriCasa Behavioral Health Consulting). All rights reserved.</p>
            <p className="footer-certifications">WOSB • EDWOSB • MBE Certification Pending</p>
          </div>
        </div>
      </footer>

      <hr className="afri-divider" data-testid="ebook-divider-footer-marquee" />

      {/* Scrolling services marquee (matches homepage placement — above footer line) */}
      <ServicesMarquee />

      <BackToTop />
    </div>
  );
};

export default EbookSalesPage;
